import '../../card/card.css'
import '../../submission/submission.css'
import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import CoverDisplay from '../../components/ImagesPage';
import BannerImage from '../../assets/Space.jpg';

const TAG_LAYOUT_STORAGE_KEY = 'at_dashboard_tag_layout';
const TAG_GROUPS_STORAGE_KEY = 'at_dashboard_tag_groups';
const DEFAULT_GROUP = 'General';

const parseTagColors = (tagColor = '') => {
  const [backgroundColor, textColor] = String(tagColor || '').split('|');
  return {
    backgroundColor: backgroundColor || '#6c757d',
    textColor: textColor || '#ffffff',
  };
};

function DashboardTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManageTools, setShowManageTools] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTagCards, setSelectedTagCards] = useState([]);
  const [selectedTagTotals, setSelectedTagTotals] = useState(null);
  const [tagWallLoading, setTagWallLoading] = useState(false);
  const [tagLayout, setTagLayout] = useState({});
  const [groupNames, setGroupNames] = useState([DEFAULT_GROUP]);
  const [draggedTagId, setDraggedTagId] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6c757d');
  const [newTagTextColor, setNewTagTextColor] = useState('#ffffff');
  const [tagMessage, setTagMessage] = useState('');
  const [selectedTagColor, setSelectedTagColor] = useState('#6c757d');
  const [selectedTagTextColor, setSelectedTagTextColor] = useState('#ffffff');

  const saveLayoutToStorage = (layout, groups) => {
    localStorage.setItem(TAG_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    localStorage.setItem(TAG_GROUPS_STORAGE_KEY, JSON.stringify(groups));
  };

  const normalizeLayout = (incomingTags, existingLayout = {}, existingGroups = [DEFAULT_GROUP]) => {
    const nextGroups = existingGroups.length > 0 ? existingGroups : [DEFAULT_GROUP];
    const nextLayout = { ...existingLayout };
    const orderCounts = {};

    nextGroups.forEach((groupName) => {
      orderCounts[groupName] = 0;
    });

    incomingTags.forEach((tag) => {
      const existingTagLayout = nextLayout[tag.tag_id];
      const groupName = existingTagLayout?.group && nextGroups.includes(existingTagLayout.group)
        ? existingTagLayout.group
        : DEFAULT_GROUP;

      if (typeof orderCounts[groupName] !== 'number') {
        orderCounts[groupName] = 0;
      }

      nextLayout[tag.tag_id] = {
        group: groupName,
        order: typeof existingTagLayout?.order === 'number' ? existingTagLayout.order : orderCounts[groupName],
      };

      orderCounts[groupName] += 1;
    });

    return {
      nextLayout,
      nextGroups,
    };
  };

  const persistLayoutState = (layout, groups) => {
    setTagLayout(layout);
    setGroupNames(groups);
    saveLayoutToStorage(layout, groups);
  };

  const reindexGroup = (layout, groupName) => {
    const entries = Object.entries(layout)
      .filter(([, value]) => value.group === groupName)
      .sort((a, b) => a[1].order - b[1].order);

    entries.forEach(([tagId], index) => {
      layout[tagId] = {
        ...layout[tagId],
        order: index,
      };
    });

    return layout;
  };

  const getGroupedTags = () => {
    const groups = {};

    groupNames.forEach((groupName) => {
      groups[groupName] = [];
    });

    tags.forEach((tag) => {
      const layout = tagLayout[tag.tag_id] || { group: DEFAULT_GROUP, order: 0 };
      const groupName = groupNames.includes(layout.group) ? layout.group : DEFAULT_GROUP;

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push({
        ...tag,
        group: groupName,
        order: layout.order,
      });
    });

    Object.keys(groups).forEach((groupName) => {
      groups[groupName].sort((a, b) => a.order - b.order || a.tag_name.localeCompare(b.tag_name));
    });

    return groups;
  };

  const moveTag = (targetGroup, targetIndex = null, targetTagId = null) => {
    if (!draggedTagId || !tagLayout[draggedTagId]) {
      return;
    }

    const nextLayout = { ...tagLayout };
    const sourceGroup = nextLayout[draggedTagId].group || DEFAULT_GROUP;

    const sourceEntries = Object.entries(nextLayout)
      .filter(([tagId, value]) => Number(tagId) !== Number(draggedTagId) && value.group === sourceGroup)
      .sort((a, b) => a[1].order - b[1].order);

    sourceEntries.forEach(([tagId], index) => {
      nextLayout[tagId] = {
        ...nextLayout[tagId],
        order: index,
      };
    });

    const targetEntries = Object.entries(nextLayout)
      .filter(([tagId, value]) => Number(tagId) !== Number(draggedTagId) && value.group === targetGroup)
      .sort((a, b) => a[1].order - b[1].order);

    const insertIndex = targetTagId
      ? targetEntries.findIndex(([tagId]) => Number(tagId) === Number(targetTagId))
      : targetIndex;

    const normalizedInsertIndex =
      insertIndex === null || insertIndex === undefined || insertIndex < 0
        ? targetEntries.length
        : insertIndex;

    targetEntries.splice(normalizedInsertIndex, 0, [String(draggedTagId), { group: targetGroup, order: normalizedInsertIndex }]);

    targetEntries.forEach(([tagId], index) => {
      nextLayout[tagId] = {
        ...nextLayout[tagId],
        group: targetGroup,
        order: index,
      };
    });

    persistLayoutState(reindexGroup(nextLayout, sourceGroup), groupNames);
    setDraggedTagId(null);
  };

  const handleAddGroup = () => {
    const trimmedGroupName = newGroupName.trim();
    if (!trimmedGroupName || groupNames.includes(trimmedGroupName)) {
      return;
    }

    const nextGroups = [...groupNames, trimmedGroupName];
    setGroupNames(nextGroups);
    saveLayoutToStorage(tagLayout, nextGroups);
    setNewGroupName('');
  };

  const handleCreateTag = async () => {
    const trimmedTagName = newTagName.trim();
    if (!trimmedTagName) {
      setTagMessage('Enter a tag name first.');
      return;
    }

    try {
      const response = await Axios.post('https://api.alientrader.cards/tags', {
        tag_name: trimmedTagName,
        tag_color: `${newTagColor}|${newTagTextColor}`,
      });

      const createdTag = response.data?.tag;
      if (!createdTag) {
        setTagMessage('Unable to create tag.');
        return;
      }

      const nextTags = response.data.created
        ? [...tags, { ...createdTag, card_count: 0 }].sort((a, b) => a.tag_name.localeCompare(b.tag_name))
        : tags.map((tag) => (tag.tag_id === createdTag.tag_id ? { ...tag, ...createdTag } : tag));

      const { nextLayout, nextGroups } = normalizeLayout(nextTags, tagLayout, groupNames);
      setTags(nextTags);
      persistLayoutState(nextLayout, nextGroups);
      setSelectedTagId(createdTag.tag_id);
      setNewTagName('');
      setTagMessage(response.data.created ? 'Tag created.' : 'Tag already existed and was selected.');
    } catch (error) {
      console.error('Error creating tag:', error);
      setTagMessage('Unable to create tag right now.');
    }
  };

  const handleSaveTagStyle = async () => {
    if (!selectedTag) {
      setTagMessage('Select a tag first.');
      return;
    }

    try {
      const response = await Axios.put(`https://api.alientrader.cards/tags/${selectedTag.tag_id}`, {
        tag_color: `${selectedTagColor}|${selectedTagTextColor}`,
      });

      const updatedTag = response.data?.tag;
      if (!updatedTag) {
        setTagMessage('Unable to save tag style.');
        return;
      }

      setTags((prevTags) =>
        prevTags.map((tag) => (tag.tag_id === updatedTag.tag_id ? { ...tag, ...updatedTag } : tag))
      );
      setSelectedTag((prevSelectedTag) =>
        prevSelectedTag && prevSelectedTag.tag_id === updatedTag.tag_id
          ? { ...prevSelectedTag, ...updatedTag }
          : prevSelectedTag
      );
      setTagMessage('Tag style saved.');
    } catch (error) {
      console.error('Error updating tag style:', error);
      setTagMessage('Unable to save tag style right now.');
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag) {
      setTagMessage('Select a tag first.');
      return;
    }

    const confirmed = window.confirm(`Delete tag "${selectedTag.tag_name}"?`);
    if (!confirmed) {
      return;
    }

    try {
      await Axios.delete(`https://api.alientrader.cards/tags/${selectedTag.tag_id}`);

      const remainingTags = tags.filter((tag) => tag.tag_id !== selectedTag.tag_id);
      const nextLayout = { ...tagLayout };
      delete nextLayout[selectedTag.tag_id];
      persistLayoutState(nextLayout, groupNames);
      setTags(remainingTags);
      setTagMessage('Tag deleted.');

      if (remainingTags.length > 0) {
        setSelectedTagId(remainingTags[0].tag_id);
      } else {
        setSelectedTagId(null);
        setSelectedTag(null);
        setSelectedTagCards([]);
        setSelectedTagTotals(null);
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      setTagMessage('Unable to delete tag right now.');
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadTags = async () => {
      try {
        const tagResponse = await Axios.get('https://api.alientrader.cards/tags');
        const nextTags = tagResponse.data || [];
        const storedLayout = JSON.parse(localStorage.getItem(TAG_LAYOUT_STORAGE_KEY) || '{}');
        const storedGroups = JSON.parse(localStorage.getItem(TAG_GROUPS_STORAGE_KEY) || JSON.stringify([DEFAULT_GROUP]));
        const { nextLayout, nextGroups } = normalizeLayout(nextTags, storedLayout, storedGroups);

        if (mounted) {
          setTags(nextTags);
          persistLayoutState(nextLayout, nextGroups);
          if (nextTags.length > 0) {
            setSelectedTagId(nextTags[0].tag_id);
          }
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTags();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedTagId) {
      return;
    }

    let mounted = true;

    const loadSelectedTagWall = async () => {
      try {
        setTagWallLoading(true);
        const response = await Axios.get(`https://api.alientrader.cards/tags/${selectedTagId}/cards`);

        if (mounted) {
          setSelectedTag(response.data?.tag || null);
          setSelectedTagCards(response.data?.cards || []);
          setSelectedTagTotals(response.data?.totals || null);
        }
      } catch (error) {
        console.error('Error fetching selected tag wall:', error);
        if (mounted) {
          setSelectedTag(null);
          setSelectedTagCards([]);
          setSelectedTagTotals(null);
        }
      } finally {
        if (mounted) {
          setTagWallLoading(false);
        }
      }
    };

    loadSelectedTagWall();

    return () => {
      mounted = false;
    };
  }, [selectedTagId]);

  useEffect(() => {
    if (!selectedTag) {
      return;
    }

    const { backgroundColor, textColor } = parseTagColors(selectedTag.tag_color);
    setSelectedTagColor(backgroundColor);
    setSelectedTagTextColor(textColor);
  }, [selectedTag]);

  if (loading) {
    return <div>Loading tag dashboard...</div>;
  }

  const groupedTags = getGroupedTags();
  const selectedTagDisplay = selectedTag || tags.find((tag) => Number(tag.tag_id) === Number(selectedTagId)) || null;
  const selectedTagStyle = parseTagColors(selectedTagDisplay?.tag_color);

  return (
    <div className="imagesPage dashboard-tag-wall">
      <div
        className='subTopBG'
        style={{
          backgroundImage: `url(${BannerImage})`,
          backgroundPosition: 'center -40px',
        }}
      >
        <div className='actions' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className='subBack'></div>
          <div className='sub' style={{ color: '#f4f7fb' }}>
          </div>
          <div className='subEdit' style={{ width: 'auto', marginRight: '0' }}>
            <button
              type="button"
              className='cards-preset-btn'
              onClick={() => setShowManageTools((prev) => !prev)}
              style={{
                backgroundColor: showManageTools ? '#acf06c' : 'rgba(255,255,255,0.9)',
                borderColor: showManageTools ? '#acf06c' : 'rgba(255,255,255,0.9)',
                color: '#253747',
                fontWeight: 600,
              }}
            >
              Manage
            </button>
          </div>
        </div>
        <div className='subHeader'>
          {selectedTagDisplay ? selectedTagDisplay.tag_name : 'Build and browse your tag walls'}
        </div>
      </div>

      <div className='subPositionAdjust'>
        <Container fluid style={{ paddingLeft: '30px', paddingRight: '30px' }}>
          <Row style={{ marginBottom: '18px' }}>
            <Col>
              {tagMessage && (
                <div className='custom-info-misc' style={{ textAlign: 'center' }}>{tagMessage}</div>
              )}
            </Col>
          </Row>

          {showManageTools && (
            <Row style={{ marginBottom: '24px' }} className='d-flex justify-content-center'>
              <Col xl={10}>
                <div className='custom-note-box shadow-sm' style={{ padding: '16px' }}>
                  <Row style={{ marginBottom: '12px' }}>
                    <Col>
                      <div className='custom-info-header'>Tag Tools</div>
                    </Col>
                  </Row>
                  <Row style={{ marginBottom: '16px' }}>
                    <Col md={7}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={newTagName}
                          placeholder="New tag name"
                          className='edit-input'
                          onChange={(e) => setNewTagName(e.target.value)}
                          style={{ maxWidth: '220px', backgroundColor: '#fff' }}
                        />
                        <input
                          type="color"
                          value={newTagColor}
                          onChange={(e) => setNewTagColor(e.target.value)}
                          style={{ width: '48px', height: '40px', padding: '4px' }}
                          title="Tag background color"
                        />
                        <input
                          type="color"
                          value={newTagTextColor}
                          onChange={(e) => setNewTagTextColor(e.target.value)}
                          style={{ width: '48px', height: '40px', padding: '4px' }}
                          title="Tag text color"
                        />
                        <button type="button" className='cards-preset-btn' onClick={handleCreateTag}>
                          Add Tag
                        </button>
                      </div>
                    </Col>
                    <Col md={5}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={newGroupName}
                          placeholder="New tag group"
                          className='edit-input'
                          onChange={(e) => setNewGroupName(e.target.value)}
                          style={{ maxWidth: '220px', backgroundColor: '#fff' }}
                        />
                        <button type="button" className='cards-preset-btn' onClick={handleAddGroup}>
                          Add Group
                        </button>
                      </div>
                    </Col>
                  </Row>
                  {selectedTag && (
                    <Row>
                      <Col>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span className='custom-info-misc'>Selected tag style:</span>
                          <input
                            type="color"
                            value={selectedTagColor}
                            onChange={(e) => setSelectedTagColor(e.target.value)}
                            style={{ width: '48px', height: '40px', padding: '4px' }}
                            title="Tag background color"
                          />
                          <input
                            type="color"
                            value={selectedTagTextColor}
                            onChange={(e) => setSelectedTagTextColor(e.target.value)}
                            style={{ width: '48px', height: '40px', padding: '4px' }}
                            title="Tag text color"
                          />
                          <button type="button" className='cards-preset-btn' onClick={handleSaveTagStyle}>
                            Save Tag Style
                          </button>
                          <button
                            type="button"
                            className='cards-preset-btn'
                            onClick={handleDeleteTag}
                            style={{ backgroundColor: '#7a1f1f', color: '#fff', borderColor: '#7a1f1f' }}
                          >
                            Delete Tag
                          </button>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 14px',
                              borderRadius: '999px',
                              border: `1px solid ${selectedTagColor}`,
                              backgroundColor: selectedTagColor,
                              color: selectedTagTextColor,
                              fontWeight: 600,
                            }}
                          >
                            {selectedTag.tag_name}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  )}
                </div>
              </Col>
            </Row>
          )}

          {groupNames.map((groupName) => (
            <div key={groupName} style={{ marginBottom: '24px' }}>
              <Row className='d-flex justify-content-center'>
                <Col xl={10}>
                  <div
                    className='custom-note-box shadow-sm'
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '12px',
                      minHeight: '60px',
                      padding: '16px',
                      borderRadius: '16px',
                      backgroundColor: '#ffffff',
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => moveTag(groupName)}
                  >
                    {(groupedTags[groupName] || []).map((tag) => (
                      <div
                        key={tag.tag_id}
                        draggable
                        onDragStart={() => setDraggedTagId(tag.tag_id)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => moveTag(groupName, null, tag.tag_id)}
                      >
                        {(() => {
                          const { backgroundColor, textColor } = parseTagColors(tag.tag_color);
                          return (
                            <button
                              type="button"
                              onClick={() => setSelectedTagId(tag.tag_id)}
                              style={{
                                border: `1px solid ${backgroundColor}`,
                                backgroundColor: selectedTagId === tag.tag_id ? backgroundColor : '#f3f6f8',
                                color: selectedTagId === tag.tag_id ? textColor : '#24323d',
                                boxShadow: selectedTagId === tag.tag_id ? '0 8px 20px rgba(36,50,61,0.16)' : 'none',
                                fontWeight: 600,
                                borderRadius: '999px',
                                padding: '10px 14px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                              }}
                            >
                              <span>{tag.tag_name}</span>
                              <span style={{ opacity: 0.85, fontWeight: 400 }}>
                                {tag.card_count || 0}
                              </span>
                            </button>
                          );
                        })()}
                      </div>
                    ))}
                    {(groupedTags[groupName] || []).length === 0 && (
                      <div className='custom-info-misc'>Drop tags here.</div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          ))}

          <Row className='d-flex justify-content-center' style={{ marginTop: '8px', marginBottom: '28px' }}>
            <Col xs="10" sm="8" lg="6" xl="5" className='custom-info-box shadow-sm'>
              <Row><div className='custom-info-header'>Selected Tag</div></Row>
              <Row className='d-flex align-items-center justify-content-center'>
                <Col><div className='custom-info-title'>Name:</div></Col>
                <Col xs={7}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      borderRadius: '999px',
                      border: `1px solid ${selectedTagStyle.backgroundColor}`,
                      backgroundColor: selectedTagStyle.backgroundColor,
                      color: selectedTagStyle.textColor,
                      fontWeight: 600,
                    }}
                  >
                    {selectedTagDisplay?.tag_name || 'None selected'}
                  </div>
                </Col>
              </Row>
              <Row className='d-flex align-items-center justify-content-center'>
                <Col><div className='custom-info-title'>Cards:</div></Col>
                <Col xs={3}><div className='custom-info-val'>{selectedTagCards.length}</div></Col>
              </Row>
              <Row className='d-flex align-items-center justify-content-center'>
                <Col><div className='custom-info-title'>Group:</div></Col>
                <Col xs={5}><div className='custom-info-val'>{tagLayout[selectedTagId]?.group || '-'}</div></Col>
              </Row>
              <Row><br /></Row>
            </Col>
          </Row>

          <Row className='d-flex justify-content-center' style={{ marginTop: '28px' }}>
            <Col xl={10} className='custom-info-box shadow-sm'>
              <Row>
                <div className='custom-info-header'>
                  {selectedTag?.tag_name || 'Select a tag'}
                </div>
              </Row>
              <Row>
                <div className='custom-info-misc' style={{ paddingBottom: '12px' }}>
                  {tagWallLoading
                    ? 'Loading cards...'
                    : selectedTag
                      ? `${selectedTagCards.length} cards${selectedTagTotals ? ` | Cost $${Number(selectedTagTotals.total_cost || 0).toFixed(2)} | Sold $${Number(selectedTagTotals.total_sold_price || 0).toFixed(2)} | Profit $${Number(selectedTagTotals.total_profit || 0).toFixed(2)}` : ''}`
                      : 'Choose a tag above to load its wall.'}
                </div>
              </Row>
              <Row className='subCardWall d-flex align-items-center justify-content-center'>
                {tagWallLoading ? (
                  <Col>
                    <div className='custom-info-misc'>Loading card wall...</div>
                  </Col>
                ) : selectedTagCards.length > 0 ? (
                  selectedTagCards.map((item, index) => (
                    <Col className='imagesPageRow d-flex align-items-center justify-content-center' key={`${selectedTagId}-${item.card_id}`}>
                      <CoverDisplay key={index} image={item} />
                    </Col>
                  ))
                ) : (
                  selectedTag && (
                    <Col>
                      <div className='custom-info-misc'>No cards assigned to this tag yet.</div>
                    </Col>
                  )
                )}
              </Row>
            </Col>
          </Row>

          {tags.length === 0 && (
            <div className='custom-info-misc' style={{ textAlign: 'center', marginTop: '20px' }}>No tags found yet.</div>
          )}
        </Container>
      </div>
    </div>
  );
}

export default DashboardTags;
